import { formatDate } from '@crm/shared';
import { Badge, Button, Card, CardContent, Spinner } from '@crm/ui';
import { Plus, ShieldCheck, UserRound } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, FormField, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { type SaasUser, useCreateSaasUser, useSaasUsers, useUpdateSaasUser } from '../api';

const VAZIO = { name: '', email: '', password: '' };

function Linha({ user, ativos }: { user: SaasUser; ativos: number }) {
  const alterar = useUpdateSaasUser();
  const ultimo = user.isActive && ativos <= 1;

  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <UserRound className="h-5 w-5" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 font-medium">
          {user.name}
          {user.isActive ? null : (
            <Badge variant="outline" className="border-destructive/50 text-destructive">
              Inativo
            </Badge>
          )}
        </p>
        <p className="text-sm text-muted-foreground">{user.email}</p>
        <p className="text-xs text-muted-foreground">desde {formatDate(user.createdAt)}</p>
      </div>

      <Button
        type="button"
        variant="ghost"
        disabled={alterar.isPending || ultimo}
        onClick={() => alterar.mutate({ id: user.id, isActive: !user.isActive })}
      >
        {alterar.isPending ? <Spinner /> : null}
        {user.isActive ? 'Desativar' : 'Reativar'}
      </Button>

      {alterar.error ? (
        <p className="w-full text-sm text-destructive">{toErrorMessage(alterar.error)}</p>
      ) : null}
    </li>
  );
}

export function SaasUsersPage() {
  useDocumentTitle('Usuarios do SaaS');

  const users = useSaasUsers();
  const criar = useCreateSaasUser();
  const [form, setForm] = useState(VAZIO);
  const [abrindo, setAbrindo] = useState(false);

  const lista = users.data?.data ?? [];
  const ativos = lista.filter((user) => user.isActive).length;
  const completo =
    form.name.trim().length >= 2 && form.email.includes('@') && form.password.length >= 8;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={<ShieldCheck />}
        title="Usuarios do SaaS"
        description="Quem administra o sistema e ve todos os assinantes."
        actions={
          abrindo ? null : (
            <Button type="button" onClick={() => setAbrindo(true)}>
              <Plus className="h-4 w-4" />
              Novo administrador
            </Button>
          )
        }
      />

      {abrindo ? (
        <Card variant="flush">
          <CardContent className="space-y-4 py-5">
            <p className="text-sm font-medium">Novo administrador</p>

            <div className="grid gap-3 sm:grid-cols-3">
              <FormField
                label="Nome"
                name="saas-user-name"
                value={form.name}
                onChange={(event) => setForm((atual) => ({ ...atual, name: event.target.value }))}
              />
              <FormField
                label="E-mail"
                name="saas-user-email"
                type="email"
                value={form.email}
                onChange={(event) => setForm((atual) => ({ ...atual, email: event.target.value }))}
              />
              <FormField
                label="Senha"
                name="saas-user-password"
                type="password"
                placeholder="8 caracteres ou mais"
                value={form.password}
                onChange={(event) =>
                  setForm((atual) => ({ ...atual, password: event.target.value }))
                }
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                disabled={!completo || criar.isPending}
                onClick={() =>
                  criar.mutate(form, {
                    onSuccess: () => {
                      setForm(VAZIO);
                      setAbrindo(false);
                    },
                  })
                }
              >
                {criar.isPending ? <Spinner /> : <Plus className="h-4 w-4" />}
                Cadastrar
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setForm(VAZIO);
                  setAbrindo(false);
                }}
              >
                Cancelar
              </Button>
            </div>

            {criar.error ? (
              <p className="text-sm text-destructive">{toErrorMessage(criar.error)}</p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      <Card variant="flush">
        <CardContent className="py-4">
          {users.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : lista.length === 0 ? (
            <EmptyState icon={<ShieldCheck />} title="Nenhum administrador cadastrado" />
          ) : (
            <ul className="divide-y divide-border">
              {lista.map((user) => (
                <Linha key={user.id} user={user} ativos={ativos} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
